import { Link } from 'react-router-dom'

const guide = {
  slug: 'block-nsfw-on-reddit-permanently',
  category: 'device',
  kicker: 'Reddit · NSFW',
  title: 'How to block NSFW on Reddit permanently — and why the toggle keeps coming back',
  seoTitle: 'Block NSFW on Reddit Permanently (iPhone, Mac, PC)',
  description: `Reddit's mature-content toggle is your own setting, so it turns back on. Why it leaks (logged out, second account) and the device-level blocks that make it stay.`,
  published: '2026-09-03',
  minutes: 6,
  intro: `Reddit is the platform people quitting porn mention most, because it
          is both useful and full of the thing they're quitting. The NSFW
          toggle in your account is the obvious first move and it is worth
          flipping. It is also a setting you own, on an account you own, and
          that is why it keeps turning back on. Here is what the setting
          really does, where it leaks, and what holds.`,
  next: ['block-porn-on-x-twitter', 'dns-porn-blocker', 'porn-blocker-you-cant-uninstall'],
  body: (
    <>
      <h2>Step 1 — The account setting</h2>
      <p>
        On the web, open Reddit's <b>Settings → Preferences</b> and turn off
        the option to <b>show mature (18+) content</b>. Reddit stops showing
        posts and communities tagged NSFW in your feed and in search while
        you're logged in to that account. Some versions of the app hide this
        toggle; the web version always has it. Note that "blur" is not the
        same as "hide": you want the content excluded, not blurred.
      </p>

      <h2>Why it keeps coming back</h2>
      <ul>
        <li><b>It's your setting.</b> The same screen turns it back on, and at 2am you know where it is.</li>
        <li><b>Logged out, it's gone.</b> Open reddit.com without logging in, or in a private window, and the account preference doesn't apply.</li>
        <li><b>A second account.</b> Making one takes a minute.</li>
        <li><b>Direct links.</b> A URL to an NSFW subreddit pasted into the browser bypasses the feed filter, with at most a confirmation click.</li>
      </ul>
      <p>
        None of this is Reddit doing anything wrong: the toggle is a
        preference, not a restriction. Preferences you set are preferences
        you unset.
      </p>

      <h2>Step 2 — Block it at the device, not in the account</h2>
      <p>
        A restriction that holds has to live outside the account you
        control. There are two levels, and it's worth being honest about
        what each can and can't do.
      </p>
      <h3>Block adult content everywhere, keep Reddit</h3>
      <p>
        A DNS category filter refuses pornography domains for every app on
        the device, including links opened from Reddit and the sites that
        NSFW posts point to. It does not filter <em>inside</em> reddit.com:
        DNS works on domains, not on posts, and Reddit hosts its own images
        and video. So a category filter closes the doors that lead
        <em> out</em> of Reddit, and Reddit's own NSFW media stays on your
        account toggle. That's a real gain, and an incomplete one.
        The mechanism: <Link to="/guides/dns-porn-blocker">DNS porn blockers explained</Link>.
      </p>
      <h3>Block Reddit itself</h3>
      <p>
        If Reddit is where your relapses start, the honest move is to block
        the domain. From your SELFCNTRL account you can add <b>reddit.com</b>
        as a blocked domain, or block the whole <b>social</b> category, and
        it applies to every app and browser on every locked device,
        immediately. Loosening it back waits twenty-four hours. If nights are
        the problem rather than Reddit itself, a night curfew cuts the
        internet entirely between, say, 11pm and 7am, Reddit included.
      </p>

      <h2>Step 3 — Make the block stick</h2>
      <p>
        A domain block you can remove in a minute is the account toggle
        again, one level down. On iPhone and iPad, SELFCNTRL's filter is
        carried by a supervised profile that can't be removed from the device
        and forbids adding a VPN; blocking more is instant, blocking less
        waits a day. How that works:
        <Link to="/guides/supervised-iphone-explained"> supervised iPhone explained</Link>.
        On Mac and Windows the filter is tied to your account with the same
        delay and with curfews.
      </p>

      <h2>What about the Reddit app specifically?</h2>
      <p>
        The app follows the account setting and the device's DNS. If
        reddit.com is blocked at DNS level, the app stops loading too, since
        it talks to the same domains. There is no supported way to block
        "only NSFW inside the app" at the system level; anyone claiming to
        filter individual posts is intercepting your traffic, which is a
        privacy trade you should know you're making.
      </p>

      <h2>Side by side</h2>
      <table>
        <thead>
          <tr><th></th><th>Account toggle</th><th>DNS category filter</th><th>Block reddit.com (locked)</th></tr>
        </thead>
        <tbody>
          <tr><td>Hides NSFW in your feed</td><td>Yes, logged in</td><td>No</td><td>Reddit unavailable</td></tr>
          <tr><td>Blocks the sites NSFW posts link to</td><td>No</td><td>Yes</td><td>Yes</td></tr>
          <tr><td>Survives logging out / second account</td><td>No</td><td>Yes</td><td>Yes</td></tr>
          <tr><td>Reddit still usable</td><td>Yes</td><td>Yes</td><td>No</td></tr>
          <tr><td>Off in under a minute</td><td>Yes</td><td>Depends on the attachment</td><td>No — 24-hour delay</td></tr>
        </tbody>
      </table>
    </>
  ),
  faq: [
    {
      q: 'Why does NSFW keep turning on in Reddit?',
      a: `Because the mature-content option is a preference on your account,
          not a restriction on your device: you can flip it back, and it
          doesn't apply when you're logged out or on another account. Only
          a restriction outside the account holds.`
    },
    {
      q: 'Can I block NSFW subreddits but keep the rest of Reddit?',
      a: `Inside Reddit, only with the account toggle, which you control.
          At the device level, DNS can block the sites NSFW posts link to,
          or block reddit.com entirely, but it cannot filter individual
          subreddits, because Reddit serves them all from the same domains.`
    },
    {
      q: 'Does blocking reddit.com also block the Reddit app?',
      a: `Yes. The app uses the same domains, so a DNS block on reddit.com
          stops it loading. If you'd rather keep Reddit and lose the nights,
          a curfew cuts the whole internet on a schedule instead.`
    },
    {
      q: 'Will Reddit see that I\'m blocking it?',
      a: `No. A DNS block means your device never asks for the site;
          nothing is sent to Reddit at all. Nothing is stored about it on
          our side either.`
    }
  ]
}

export default guide
